import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  createTheme,
  ThemeProvider,
  useTheme,
  Autocomplete,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  PhoneAndroid as PhoneAndroidIcon,
  Fax as FaxIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
} from "@mui/icons-material";
import axios from "axios";
import config from "../../config/config.js";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { toast } from "react-toastify";
import { useDrawer } from "../../components/DrawerContext.jsx";
import { useAuth } from "../login/AuthContext";

const CustomerForm = ({
  onCustomerCreated,
  editingCustomer,
  fetchCustomers,
}) => {
  const { userData, token } = useAuth();
  const initialState = {
    Unvan: "",
    Yetkili: "",
    Tel: "",
    Tel2: "",
    Fax: "",
    VergiDairesi: "",
    VergiNo: "",
    TCKimlikNo: "",
    EPosta: "",
    Il: "",
    Ilce: "",
    Adres: "",
    BayiKodu: "",
  };

  const [data, setData] = useState(editingCustomer || initialState);
  const [errors, setErrors] = useState({});
  const { mode } = useDrawer();

  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [selectedIlId, setSelectedIlId] = useState("");

  // Gelişmiş telefon numarası kontrolü
  const isValidPhoneNumber = (phone) => {
    if (!phone) return false;

    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

    if (!/^\d+$/.test(cleanPhone)) return false;
    if (cleanPhone.length < 9 || cleanPhone.length > 11) return false;

    if (cleanPhone.length === 11) {
      if (!cleanPhone.startsWith('0')) return false;

      if (cleanPhone.startsWith('05')) {
        const operator = cleanPhone.substring(2, 4);

        const validOperators = ['05', '06', '07', '08', '09',
                                '30', '31', '32', '33', '34', '35', '36', '37', '38', '39',
                                '40', '41', '42', '43', '44', '45', '46', '47', '48', '49',
                                '50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
        return validOperators.includes(operator);
      }

      const areaCode = cleanPhone.substring(1, 4);
      return parseInt(areaCode) >= 212 && parseInt(areaCode) <= 488;
    }

    if (cleanPhone.length === 10) {
      if (cleanPhone.startsWith('5')) {
        const operator = cleanPhone.substring(1, 3);
        const validOperators = ['05', '06', '07', '08', '09',
                                '30', '31', '32', '33', '34', '35', '36', '37', '38', '39',
                                '40', '41', '42', '43', '44', '45', '46', '47', '48', '49',
                                '50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
        return validOperators.includes(operator);
      }
      return false;
    }

    if (cleanPhone.length === 9) {
      const areaCode = cleanPhone.substring(0, 3);
      return parseInt(areaCode) >= 212 && parseInt(areaCode) <= 488;
    }

    return false;
  };

  // TC Kimlik No doğrulama algoritması
  const isValidTCKimlikNo = (tcNo) => {
    if (!tcNo || typeof tcNo !== 'string') return false;

    const tc = tcNo.trim();

    if (!/^\d{11}$/.test(tc)) return false;
    if (tc[0] === '0') return false;

    const digits = tc.split('').map(Number);

    let sum1 = 0;
    let sum2 = 0;

    for (let i = 0; i < 9; i += 2) {
      sum1 += digits[i];
    }

    for (let i = 1; i < 9; i += 2) {
      sum2 += digits[i];
    }

    const check10 = ((sum1 * 7) - sum2) % 10;
    if (check10 !== digits[9]) return false;

    const check11 = (sum1 + sum2 + digits[9]) % 10;
    if (check11 !== digits[10]) return false;

    return true;
  };

  useEffect(() => {
    const fetchIller = async () => {
      try {
        const url = config.baseURL.endsWith("/")
          ? `${config.baseURL}musteriler/form-bilgi/iller`
          : `${config.baseURL}/musteriler/form-bilgi/iller`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP hatası: ${response.status}`);
        }
        const data = await response.json();
        setIller(data);
      } catch (error) {
        console.error("İller yüklenirken hata:", error);
      }
    };

    fetchIller();
  }, []);

  useEffect(() => {
    const fetchIlceler = async () => {
      if (!selectedIlId) {
        setIlceler([]);
        return;
      }

      try {
        const url = config.baseURL.endsWith("/")
          ? `${config.baseURL}musteriler/form-bilgi/ilceler/${selectedIlId}`
          : `${config.baseURL}/musteriler/form-bilgi/ilceler/${selectedIlId}`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP hatası: ${response.status}`);
        }

        const data = await response.json();
        setIlceler(data);
      } catch (error) {
        console.error("İlçeler yüklenirken hata:", error);
        setIlceler([]);
      }
    };

    fetchIlceler();
  }, [selectedIlId, token]);

  const handleIlChange = (event) => {
    const selectedValue = event.target.value;
    const selectedIl = iller.find((il) => il.IlAdi === selectedValue);

    setData((prevData) => ({
      ...prevData,
      Il: selectedValue,
      Ilce: "",
    }));

    if (errors.Il) {
      setErrors((prevErrors) => ({ ...prevErrors, Il: "" }));
    }
    if (errors.Ilce) {
      setErrors((prevErrors) => ({ ...prevErrors, Ilce: "" }));
    }

    setSelectedIlId(selectedIl ? selectedIl.PlakaKodu || selectedIl.IlId : "");
  };

  useEffect(() => {
    if (editingCustomer && editingCustomer.Il) {
      const existingIl = iller.find((il) => il.IlAdi === editingCustomer.Il);
      if (existingIl) {
        setSelectedIlId(existingIl.PlakaKodu || existingIl.IlId);
      }
    }
  }, [editingCustomer, iller]);

  const handleIlceChange = (event) => {
    const selectedValue = event.target.value;

    setData((prevData) => ({
      ...prevData,
      Ilce: selectedValue,
    }));

    if (errors.Ilce) {
      setErrors((prevErrors) => ({ ...prevErrors, Ilce: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const {
      Unvan,
      Yetkili,
      Tel,
      Tel2,
      Fax,
      VergiDairesi,
      VergiNo,
      TCKimlikNo,
      EPosta,
      Il,
      Ilce,
      Adres
    } = data;

    // Yardımcı fonksiyonlar
    const isEmpty = (v) => !v?.toString().trim();
    const isDigits = (v, len) => new RegExp(`^\\d{${len}}$`).test(v);
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    // Unvan & Yetkili
    if (isEmpty(Unvan)) newErrors.Unvan = "Ünvan alanı zorunludur";
    if (isEmpty(Yetkili)) newErrors.Yetkili = "Yetkili alanı zorunludur";

    // GSM veya Telefon - en az biri
    if (isEmpty(Tel) && isEmpty(Tel2)) {
      newErrors.Tel = "GSM veya Telefon alanlarından en az biri doldurulmalıdır";
      newErrors.Tel2 = "GSM veya Telefon alanlarından en az biri doldurulmalıdır";
    } else {
      // Tel (Telefon) kontrolü
      if (Tel && !isEmpty(Tel)) {
        if (!isValidPhoneNumber(Tel.trim())) {
          newErrors.Tel = "Geçerli bir GSM numarası giriniz (örn: 0535 555 5555)";
        }
      }

      // Tel2 (GSM) kontrolü
      if (Tel2 && !isEmpty(Tel2)) {
        if (!isValidPhoneNumber(Tel2.trim())) {
          newErrors.Tel2 = "Geçerli bir telefon numarası giriniz (örn: 0212 555 5555 veya 0535 555 5555)";
        }
      }
    }

    // Fax (opsiyonel, ama girildiyse kontrol et)
    if (Fax && !isEmpty(Fax)) {
      if (!isValidPhoneNumber(Fax.trim()) && Fax.trim().length < 7) {
        newErrors.Fax = "Geçerli bir fax numarası giriniz (en az 7 haneli)";
      }
    }

    // VergiDairesi
    if (isEmpty(VergiDairesi)) {
      newErrors.VergiDairesi = "Vergi Dairesi alanı zorunludur";
    }

    // VergiNo veya TC Kimlik No - en az biri + detaylı kontrol
    if (isEmpty(VergiNo) && isEmpty(TCKimlikNo)) {
      newErrors.VergiNo = "Vergi No veya TC Kimlik No alanlarından en az biri doldurulmalıdır";
      newErrors.TCKimlikNo = "Vergi No veya TC Kimlik No alanlarından en az biri doldurulmalıdır";
    } else {
      // Vergi No kontrolü
      if (VergiNo && !isEmpty(VergiNo)) {
        const cleanVergiNo = VergiNo.trim();
        if (!isDigits(cleanVergiNo, 10)) {
          newErrors.VergiNo = "Vergi No 10 haneli rakam olmalıdır";
        }
      }

      // TC Kimlik No kontrolü
      if (TCKimlikNo && !isEmpty(TCKimlikNo)) {
        if (!isValidTCKimlikNo(TCKimlikNo.trim())) {
          newErrors.TCKimlikNo = "Geçerli bir TC Kimlik No giriniz (11 haneli)";
        }
      }
    }

    // E-Posta
    if (isEmpty(EPosta)) {
      newErrors.EPosta = "E-Posta alanı zorunludur";
    } else if (!isValidEmail(EPosta.trim())) {
      newErrors.EPosta = "Geçerli bir E-Posta adresi giriniz";
    }

    // İl, İlçe, Adres, BayiKodu
    if (isEmpty(Il)) newErrors.Il = "İl alanı zorunludur";
    if (isEmpty(Ilce)) newErrors.Ilce = "İlçe alanı zorunludur";
    if (isEmpty(Adres)) newErrors.Adres = "Adres alanı zorunludur";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeData = (data) => {
    const normalized = {
      Unvan: data.Unvan ?? "",
      Yetkili: data.Yetkili ?? "",
      Tel: data.Tel ?? "",
      Tel2: data.Tel2 ?? "",
      Fax: data.Fax ?? "",
      VergiDairesi: data.VergiDairesi ?? "",
      VergiNo: data.VergiNo ?? "",
      TCKimlikNo: data.TCKimlikNo ?? "",
      EPosta: data.EPosta ?? "",
      Il: data.Il ?? "",
      Ilce: data.Ilce ?? "",
      Adres: data.Adres ?? "",
      BayiKodu: data.BayiKodu ?? userData.BayiKodu,
    };

    if (normalized.Il && iller.length > 0) {
      const correctIl = iller.find(
        (il) => il.IlAdi?.toLocaleLowerCase("tr-TR") === normalized.Il?.toLocaleLowerCase("tr-TR")
      );
      if (correctIl) {
        normalized.Il = correctIl.IlAdi;
        setSelectedIlId(correctIl.PlakaKodu || correctIl.IlId);
      }
    }

    if (normalized.Ilce && ilceler.length > 0) {
      const correctIlce = ilceler.find(
        (ilce) => ilce.IlceAdi?.toLocaleLowerCase("tr-TR") === normalized.Ilce?.toLocaleLowerCase("tr-TR")
      );
      if (correctIlce) {
        normalized.Ilce = correctIlce.IlceAdi;
      }
    }

    return normalized;
  };

  useEffect(() => {
    if (!editingCustomer) {
      setData(initialState);
    }
    if (editingCustomer) {
      const normalizedData = normalizeData(editingCustomer);
      setData(normalizedData);
    }
  }, [editingCustomer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // DÜZELTME: validateForm() fonksiyonunu çağır
    const isValid = validateForm();

    if (!isValid) {
      // İlk hatayı toast ile göster
      const firstError = Object.values(errors)[0];
      if (firstError) {
        toast.warning(firstError);
      }

      // Tüm hataları console'a yazdır (development için)


      // İşlemi durdur
      return;
    }

    try {
      if (mode === "edit") {
        const { Id, ...updateData } = data;
        const res = await axios.put(
          `${config.baseURL}/musteriler/${editingCustomer.Id}`,
          updateData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.status === 200) {
          toast.success("Müşteri başarıyla güncellendi");
          onCustomerCreated(res.data);
          if (typeof fetchCustomers === "function") {
            fetchCustomers();
          }
        }
      } else {
        const newCustomerData = {
          ...data,
          BayiKodu: userData.BayiKodu,
        };

        const res = await axios.post(
          `${config.baseURL}/musteriler`,
          newCustomerData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.status === 200) {
          onCustomerCreated(res.data);
          setData(initialState);
          toast.success("Müşteri başarıyla eklendi.");
          if (typeof fetchCustomers === "function") {
            fetchCustomers();
          }
        }
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message || "Bir hata oluştu.");
      } else {
        console.error("İşlem başarısız:", error);
        toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      }
    }
  };

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": outerTheme.palette.mode === 'dark' ? "#424242" : "#D0D0D0",
              "--TextField-brandBorderHoverColor": outerTheme.palette.mode === 'dark' ? "#616161" : "#9E9E9E",
              "--TextField-brandBorderFocusedColor": outerTheme.palette.mode === 'dark' ? "#757575" : "#616161",
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
              "& input": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
              "& textarea": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              "& .MuiSelect-select": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: outerTheme.palette.mode === 'dark'
                ? "0 1px 3px rgba(0,0,0,0.3)"
                : "0 1px 3px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              border: outerTheme.palette.mode === 'dark'
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
              backgroundColor: outerTheme.palette.mode === 'dark'
                ? outerTheme.palette.background.paper
                : "#FFFFFF",
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: outerTheme.palette.mode === 'dark'
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            },
          },
        },
      },
    });

  const outerTheme = useTheme();

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
      <Icon
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.54)',
          fontSize: '20px'
        }}
      />
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color="text.secondary"
        sx={{
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <form onSubmit={handleSubmit} id="musterikaydetform">
          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={BusinessIcon} title="Temel Bilgiler" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Ünvan"
                  variant="outlined"
                  required
                  name="Unvan"
                  value={data.Unvan}
                  onChange={handleInputChange}
                  error={!!errors.Unvan}
                  helperText={errors.Unvan}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Yetkili"
                  variant="outlined"
                  required
                  name="Yetkili"
                  value={data.Yetkili}
                  onChange={handleInputChange}
                  error={!!errors.Yetkili}
                  helperText={errors.Yetkili}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={PhoneIcon} title="İletişim Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GSM"
                  variant="outlined"
                  name="Tel"
                  value={data.Tel}
                  inputProps={{
                    maxLength: 11,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 11) {
                      handleInputChange({ target: { name: "Tel", value } });
                    }
                  }}
                  error={!!errors.Tel}
                  helperText={errors.Tel}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Telefon"
                  variant="outlined"
                  name="Tel2"
                  value={data.Tel2}
                  inputProps={{
                    maxLength: 11,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 11) {
                      handleInputChange({ target: { name: "Tel2", value } });
                    }
                  }}
                  error={!!errors.Tel2}
                  helperText={errors.Tel2}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Fax"
                  variant="outlined"
                  name="Fax"
                  value={data.Fax}
                  inputProps={{
                    maxLength: 11,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 11) {
                      handleInputChange({ target: { name: "Fax", value } });
                    }
                  }}
                  error={!!errors.Fax}
                  helperText={errors.Fax}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaxIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="E-Posta"
                  variant="outlined"
                  required
                  name="EPosta"
                  value={data.EPosta}
                  type="email"
                  error={!!errors.EPosta}
                  helperText={errors.EPosta}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange({ target: { name: "EPosta", value } });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={ReceiptIcon} title="Vergi Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Vergi Dairesi"
                  variant="outlined"
                  required
                  name="VergiDairesi"
                  value={data.VergiDairesi}
                  onChange={handleInputChange}
                  error={!!errors.VergiDairesi}
                  helperText={errors.VergiDairesi}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Vergi No"
                  variant="outlined"
                  name="VergiNo"
                  value={data.VergiNo}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 10) {
                      handleInputChange({ target: { name: "VergiNo", value } });
                    }
                  }}
                  error={!!errors.VergiNo}
                  helperText={errors.VergiNo}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="TC Kimlik No"
                  variant="outlined"
                  name="TCKimlikNo"
                  value={data.TCKimlikNo}
                  inputProps={{
                    maxLength: 11,
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (value.length <= 11) {
                      handleInputChange({ target: { name: "TCKimlikNo", value } });
                    }
                  }}
                  error={!!errors.TCKimlikNo}
                  helperText={errors.TCKimlikNo}
                />
              </Grid>
            </Grid>
            </CardContent>
          </Card>

          <Card elevation={0}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={LocationIcon} title="Adres Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={iller}
                  getOptionLabel={(option) => option.IlAdi || ""}
                  value={iller.find((il) => il.IlAdi.toLocaleLowerCase("tr-TR") === data.Il.toLocaleLowerCase("tr-TR")) || null}
                  onChange={(event, newValue) => {
                    handleIlChange({
                      target: {
                        name: "Il",
                        value: newValue ? newValue.IlAdi : "",
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="İl"
                      variant="outlined"
                      required
                      error={!!errors.Il}
                      helperText={errors.Il}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  disabled={!selectedIlId}
                  options={ilceler}
                  getOptionLabel={(option) => option.IlceAdi || ""}
                  value={ilceler.find((ilce) => ilce.IlceAdi.toLocaleLowerCase("tr-TR") === data.Ilce.toLocaleLowerCase("tr-TR")) || null}
                  onChange={(event, newValue) => {
                    handleIlceChange({
                      target: {
                        name: "Ilce",
                        value: newValue ? newValue.IlceAdi : "",
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="İlçe"
                      variant="outlined"
                      required
                      error={!!errors.Ilce}
                      helperText={errors.Ilce}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <LocationIcon color="action" fontSize="small" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Adres"
                  variant="outlined"
                  required
                  name="Adres"
                  value={data.Adres}
                  onChange={handleInputChange}
                  error={!!errors.Adres}
                  helperText={errors.Adres}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1 }}>
                        <HomeIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            </CardContent>
          </Card>
        </form>
      </Box>
    </ThemeProvider>
  );
};

export default CustomerForm;